import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { AccountActivePage, getAccountCustomPages } from "@shopflo/ui/account-page";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { mutate } from "swr";
import { useSearchParams } from "react-router-dom";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import { postRequest, getRequest, putRequest, deleteRequest } from "lib/core/apiClient";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { CouponType, ItemLevelETDType, NoteAttributesType, ShippingHandlesType } from "lib/types/checkout";
import { analyticsEvents, CombDiscountConflictCode, eventTypes } from "lib/utils/constants";
import { appendParamsToItemsUrl, findIdCouponByCode, getDiscountedProductSelectorData } from "lib/utils/checkout";
import { getInvalidityReason, publishPostMessage } from "lib/utils/helpers";
import { findDroppedNames, resolveNoteAttributes, serializeNoteAttributes } from "lib/utils/customAttributes";
import { useItemEdit } from "lib/hooks/useItemEdit";
import { usePopupOrchestrator } from "./PopupOrchestratorProvider";
import { useUserConsentContext } from "./UserConsentProvider";
import { ShopfloWindow, ShopfloWindowContext } from "lib/types/shopfloWindow";
import { ConsentUpdate } from "lib/types/consent";
import { setRuleLocalStorageItem } from "lib/utils/ruleLocalStorage";

// Coalesces a burst of add()/edit()/remove() calls into one request.
const CUSTOM_ATTRIBUTES_PUT_DELAY = 300;

// How long a queued write waits for the checkout to exist before giving up.
const MAX_NOTE_ATTRIBUTE_WAITS = 20;

// Queued as operations rather than a finished list: the merge happens at flush
// time, against whatever the checkout holds then.
type PendingNoteOps = {
  upserts: Map<string, string>;
  removals: Set<string>;
  updateOnly: Set<string>;
  waiters: Array<() => void>;
};

const getWindowContext = (page?: string | null): ShopfloWindowContext => {
  switch (page?.toLowerCase()) {
    case "cart":
      return "cart";
    case "marketing-popup":
      return "sso";
    case "account":
      return "account";
    default:
      return "checkout";
  }
};

// Page switching lives in the account page's own state, so the page hands it
// to the provider through useAccountWindowActions.
type AccountWindowBinding = {
  activePage: string;
  openPage: (page: string) => void;
  close: () => void;
};

const AccountWindowBindingContext = createContext<{ current: AccountWindowBinding | null }>({ current: null });

export const useAccountWindowActions = (binding: AccountWindowBinding) => {
  const bindingRef = useContext(AccountWindowBindingContext);
  bindingRef.current = binding;
  // Same render-time assignment as the provider, so custom page scripts read it.
  if (window.Shopflo?.context === "account") {
    window.Shopflo.state.activePage = binding.activePage;
  }
};

// Owns window.Shopflo on every page. Common state and actions are exposed
// everywhere; the rest only in the context (checkout / cart / sso / account) it belongs to.
const WindowActionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const context = getWindowContext(searchParams.get("page"));
  const contextRef = useRef(context);
  contextRef.current = context;

  const {
    state: { checkoutId, coupons, checkoutView, isC2P, rawCheckoutObject, billing, noteAttributes },
    actions: {
      updateCheckoutBasedOnCheckoutResponse,
      setCoupons,
      setCheckoutModal,
      setCheckoutView,
      setShippingHandles,
      setDiscountProductSelectorModal,
      setCouponReplacementConfig,
      setItemLevelETD,
    },
  } = useCheckoutContext();

  const {
    state: { isAuthenticated },
  } = useAuthContext();

  const {
    state: { merchant, checkoutMetadata, cartMetadata, ssoMetadata },
  } = useMerchantContext();

  const uiAttributes = {
    checkout: checkoutMetadata,
    cart: cartMetadata,
    sso: ssoMetadata?.popup,
    account: ssoMetadata?.accounts,
  }[context];

  const {
    state: { paymentMethods },
    actions: { mutatePayment },
  } = usePaymentContext();

  const {
    state: { user },
  } = useUserContext();

  const { openPopup ,closePopup} = usePopupOrchestrator();
  const { sendAnalyticsEvent, setCustomAttributes } = useSendAnalyticsEvent();

  const [isLoading, setIsLoading] = useState(false);

  const { handleAddItem, handleRemoveItem, isLoading: isAddItemLoading } = useItemEdit();

  const { initialCheckoutStep } = checkoutMetadata ?? {};

  const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);

  const applyCoupon = async (code: string): Promise<boolean> => {
    if (!code?.trim()) return false;

    const couponId = findIdCouponByCode(coupons, code);

    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_COUPON_ENTERED,
      eventType: "click",
      metaData: { couponData: { coupon_code: code, coupon_id: couponId || "NA" } },
    });

    const payload = { discount_code: code?.trim() };

    try {
      setIsLoading(true);

      let response;
      if (isAuthenticated) {
        response = await postRequest(DISCOUNT_PATH, payload, "KRATOS_PRIVATE");
        setCoupons(await getRequest(DISCOUNT_PATH, "KRATOS_PRIVATE"));
      } else {
        response = await postRequest(DISCOUNT_PATH, payload, "KRATOS_PUBLIC");
        setCoupons(await getRequest(DISCOUNT_PATH, "KRATOS_PUBLIC"));
      }

      if (!response) {
        setIsLoading(false);
        return false;
      }

      if (response?.metadata?.show_discount_warning) {
        setCouponReplacementConfig({
          showPopup: true,
          appliedDiscountCode: code?.trim(),
          invalidDiscountCodes: response?.metadata?.invalid_discount_code || [],
          invalidityReason: getInvalidityReason(response?.metadata?.invalid_discount_reasons || []),
        });
        setIsLoading(false);
        return false;
      }

      if (context === "checkout") {
        publishPostMessage(eventTypes.COUPONS_UPDATED, {});
      }
      const showProductSelector = updateCheckoutBasedOnCheckoutResponse(response, "MAIN");
      setIsLoading(false);

      if (!showProductSelector) {
        setCheckoutModal("NONE");
      }

      const isAddressServiceable = response?.pricing?.serviceable ?? false;
      if (isAuthenticated && !isAddressServiceable && context !== "cart") {
        const hasUserAddresses = user?.addresses?.find((a) => a?.source === "SHIPPING_ADDRESS");
        if (checkoutView === "PAYMENTS" && initialCheckoutStep !== "PAYMENTS") setCheckoutView("ADDRESS_LIST");
        if (checkoutView === "PAYMENTS" && initialCheckoutStep === "PAYMENTS") {
          openPopup(hasUserAddresses ? "ADDRESS_LIST" : "ADDRESS_NEW", {});
        }
        return false;
      }

      const shippingHandles = response?.metadata?.available_shipping_handles ?? [];
      const showShippingHandles = response?.metadata?.show_shipping_handle_selector ?? false;
      setShippingHandles(shippingHandles as ShippingHandlesType);

      if (shippingHandles.length > 0 && showShippingHandles && checkoutView === "PAYMENTS" && initialCheckoutStep !== "PAYMENTS") {
        setCheckoutModal("SHIPPING_HANDLES");
      } else {
        mutatePayment();
        mutate(`/checkout/${checkoutId}/rewards`);
        mutate(`UPI_INTENT`);
      }

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_COUPON_SUCCESS,
        eventType: "flo_action",
        metaData: { couponData: { coupon_code: code, coupon_id: couponId || "NA" } },
      });

      return true;
    } catch (e: any) {
      setIsLoading(false);

      const shouldShowSwitchDialogue = e?.response?.data?.error?.code === CombDiscountConflictCode;
      const invalidDiscountCode = e?.response?.data?.error?.data?.invalid_discount_codes;
      if (shouldShowSwitchDialogue) {
        setCouponReplacementConfig({
          showPopup: true,
          appliedDiscountCode: code,
          invalidDiscountCodes: invalidDiscountCode,
          invalidityReason: "DISCOUNTS_NOT_COMBINABLE",
        });
      }

      const errorMsg = e?.response?.data?.error?.message;
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_COUPON_FAILED,
        eventType: "flo_action",
        metaData: { couponData: { coupon_code: code, coupon_id: couponId || "NA", failed_reason: errorMsg } },
      });

      console.error(e);
      return false;
    }
  };

  const addNoteAttributes = (attributes: NoteAttributesType[]): Promise<void> =>
    enqueueNoteAttributes({ upserts: attributes });

  // Update-only: names the checkout does not already carry are dropped, so an
  // edit never creates. Best effort, since the merge base can be a round trip stale.
  const editNoteAttributes = (attributes: NoteAttributesType[]): Promise<void> =>
    enqueueNoteAttributes({ upserts: attributes, updateOnly: true });

  // Takes names. Objects are accepted but their value is ignored: matching on
  // value against a stale base would silently no-op, the worst outcome for a
  // control the shopper just toggled.
  const removeNoteAttributes = (attributes: Array<string | { name: string }>): Promise<void> =>
    enqueueNoteAttributes({
      removals: (attributes ?? [])
        .map((attribute) => (typeof attribute === "string" ? attribute : attribute?.name))
        .filter(Boolean),
    });

  const applyShopifyGiftCard = async (code: string): Promise<boolean> => {
    if (!code?.trim()) return false;

    const shopifyGC = paymentMethods?.find((method) => method.name === "GC" || method.name === "SHOPIFY_GC");
    if (shopifyGC && !shopifyGC.isAvailable && shopifyGC?.unavailabilityReasons?.length) {
      setCheckoutModal("GYFTR_SWITCH_COUPON_DIALOG");
      return false;
    }

    const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);

    try {
      setIsLoading(true);
      const response: any = await postRequest(`/checkout/v2/checkout/${checkoutId}/gc/voucher`, {
        voucher_code: code.trim(),
        voucher_payment_mode: "GIFTCARD",
      });

      if (response?.status === "FAILED") {
        setIsLoading(false);
        return false;
      }

      updateCheckoutBasedOnCheckoutResponse(response);
      mutate(DISCOUNT_PATH);
      mutatePayment();
      mutate(`/checkout/${checkoutId}/rewards`);
      setIsLoading(false);
      return true;
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      return false;
    }
  };

  const removeShopifyGiftCard = async (): Promise<boolean> => {
    const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
    try {
      setIsLoading(true);
      const response: any = await deleteRequest(`/checkout/v2/checkout/${checkoutId}/gc/voucher`, {
        voucher_payment_mode: "GIFTCARD",
        redemption_amount: billing?.gift_card,
      });
      updateCheckoutBasedOnCheckoutResponse(response);
      mutate(DISCOUNT_PATH);
      mutatePayment();
      mutate(`/checkout/${checkoutId}/rewards`);
      setIsLoading(false);
      return true;
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      return false;
    }
  };

  // Keep refs so window-registered wrappers always call the latest version
  // without needing to re-register on every render.
  const applyCouponRef = useRef(applyCoupon);
  applyCouponRef.current = applyCoupon;

  const addItemRef = useRef(handleAddItem);
  addItemRef.current = handleAddItem;

  const removeItemRef = useRef(handleRemoveItem);
  removeItemRef.current = handleRemoveItem;

  const addNoteAttributesRef = useRef(addNoteAttributes);
  addNoteAttributesRef.current = addNoteAttributes;

  const editNoteAttributesRef = useRef(editNoteAttributes);
  editNoteAttributesRef.current = editNoteAttributes;

  const removeNoteAttributesRef = useRef(removeNoteAttributes);
  removeNoteAttributesRef.current = removeNoteAttributes;

  const applyShopifyGiftCardRef = useRef(applyShopifyGiftCard);
  applyShopifyGiftCardRef.current = applyShopifyGiftCard;

  const removeShopifyGiftCardRef = useRef(removeShopifyGiftCard);
  removeShopifyGiftCardRef.current = removeShopifyGiftCard;

  const setItemLevelETDRef = useRef(setItemLevelETD);
  setItemLevelETDRef.current = setItemLevelETD;

  const setCustomAttributesRef = useRef(setCustomAttributes);
  setCustomAttributesRef.current = setCustomAttributes;

  const checkoutIdRef = useRef(checkoutId);
  checkoutIdRef.current = checkoutId;

  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  // What the next merge is layered on top of. Held in a ref rather than read
  // from state at merge time because a burst of writes resolves faster than
  // state re-renders, so state would be a batch behind.
  const noteAttributesMergeBaseRef = useRef<NoteAttributesType[]>(noteAttributes ?? []);
  // Names the store script has written, so a name that disappears from a later
  // snapshot can be taken off the checkout. Only that script's snapshot belongs
  // here: the imperative actions must not add to it, or the next snapshot - which
  // would not mention an attribute a custom component wrote - would count it as
  // dropped and delete it.
  const pushedNoteAttributeNamesRef = useRef<Set<string>>(new Set());
  const lastNoteAttributesPutRef = useRef<string | null>(null);
  const customAttributesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingNoteOpsRef = useRef<PendingNoteOps | null>(null);
  const noteAttributesInFlightRef = useRef<boolean>(false);
  const noteAttributesWaitsRef = useRef<number>(0);

  // A fresh checkout response is authoritative: it already contains whatever we
  // last wrote, so it replaces our local base. Skipped mid-flight, because that
  // response was built before our PUT and would move the base backwards.
  useEffect(() => {
    if (noteAttributesInFlightRef.current) return;
    noteAttributesMergeBaseRef.current = noteAttributes ?? [];
  }, [noteAttributes]);

  const updateCheckoutRef = useRef(updateCheckoutBasedOnCheckoutResponse);
  updateCheckoutRef.current = updateCheckoutBasedOnCheckoutResponse;

  const flushNoteAttributesRef = useRef<() => void>(() => {});

  const scheduleNoteAttributesFlush = () => {
    if (customAttributesTimerRef.current) {
      clearTimeout(customAttributesTimerRef.current);
    }
    customAttributesTimerRef.current = setTimeout(() => {
      flushNoteAttributesRef.current();
    }, CUSTOM_ATTRIBUTES_PUT_DELAY);
  };

  // Resolves every caller awaiting this batch. They learn that the write has
  // been attempted, not what the server stored.
  const settleNoteAttributes = (pending: PendingNoteOps) => {
    pending.waiters.forEach((resolve) => resolve());
  };

  const flushNoteAttributes = async (): Promise<void> => {
    // One request at a time: a later batch merges onto whatever the in-flight
    // one leaves behind rather than racing it.
    if (noteAttributesInFlightRef.current) {
      scheduleNoteAttributesFlush();
      return;
    }

    const pending = pendingNoteOpsRef.current;
    if (!pending) return;

    const base = noteAttributesMergeBaseRef.current;
    const checkoutIdentifier = checkoutIdRef.current;
    // A component can write before the checkout has loaded. Hold the batch and
    // retry rather than dropping it, or a write on first render is lost.
    if (!checkoutIdentifier) {
      if (noteAttributesWaitsRef.current < MAX_NOTE_ATTRIBUTE_WAITS) {
        noteAttributesWaitsRef.current += 1;
        scheduleNoteAttributesFlush();
        return;
      }
      pendingNoteOpsRef.current = null;
      noteAttributesWaitsRef.current = 0;
      console.error("Dropped note attribute write: no checkout to write to");
      settleNoteAttributes(pending);
      return;
    }

    noteAttributesWaitsRef.current = 0;
    pendingNoteOpsRef.current = null;
    noteAttributesInFlightRef.current = true;

    const merged = resolveNoteAttributes({
      base,
      upserts: Array.from(pending.upserts, ([name, value]) => ({ name, value })),
      removals: pending.removals,
      updateOnly: pending.updateOnly,
    });
    const serialized = serializeNoteAttributes(merged);

    // A no-op must return before the state update below. CustomComponentSlot
    // re-runs every merchant script whenever the checkout object changes, so a
    // component that writes as it executes would otherwise loop forever.
    if (serialized === serializeNoteAttributes(base) || serialized === lastNoteAttributesPutRef.current) {
      noteAttributesInFlightRef.current = false;
      settleNoteAttributes(pending);
      if (pendingNoteOpsRef.current) scheduleNoteAttributesFlush();
      return;
    }

    try {
      const response: any = await putRequest(
        `/checkout/${checkoutIdentifier}`,
        { note_attributes: merged },
        isAuthenticatedRef.current ? "KRATOS_PRIVATE" : "KRATOS_PUBLIC",
      );
      if (Boolean(response?.error)) {
        throw new Error(response.error);
      }
      const authoritative: NoteAttributesType[] = response?.metadata?.note_attributes ?? merged;
      // Record what the checkout actually holds, not what we asked for. If the
      // server did not take the write whole, remembering our payload would make
      // the no-op check suppress every identical retry.
      lastNoteAttributesPutRef.current = serializeNoteAttributes(authoritative);
      noteAttributesMergeBaseRef.current = authoritative;
      // Without this the rules that gate on note attributes never see the write:
      // they read rawCheckoutObject, not the normalized list.
      updateCheckoutRef.current(response);
      settleNoteAttributes(pending);
    } catch (e: any) {
      console.error(e);
      settleNoteAttributes(pending);
    } finally {
      noteAttributesInFlightRef.current = false;
      if (pendingNoteOpsRef.current) scheduleNoteAttributesFlush();
    }
  };
  flushNoteAttributesRef.current = flushNoteAttributes;

  /**
   * Queues note attribute operations rather than a finished list, so a checkout
   * response arriving before the flush rebases them instead of discarding them.
   */
  const enqueueNoteAttributes = ({
    upserts = [],
    removals = [],
    updateOnly = false,
  }: {
    upserts?: NoteAttributesType[];
    removals?: string[];
    updateOnly?: boolean;
  }): Promise<void> => {
    const pending: PendingNoteOps = pendingNoteOpsRef.current ?? {
      upserts: new Map<string, string>(),
      removals: new Set<string>(),
      updateOnly: new Set<string>(),
      waiters: [],
    };

    upserts.forEach(({ name, value }) => {
      if (!name) return;
      pending.upserts.set(name, value);
      pending.removals.delete(name);
      if (updateOnly) pending.updateOnly.add(name);
      else pending.updateOnly.delete(name);
    });

    removals.forEach((name) => {
      if (!name) return;
      pending.removals.add(name);
      pending.upserts.delete(name);
      pending.updateOnly.delete(name);
      // Hand ownership back, so the next store snapshot does not re-drop it.
      pushedNoteAttributeNamesRef.current.delete(name);
    });

    pendingNoteOpsRef.current = pending;

    return new Promise<void>((resolve) => {
      pending.waiters.push(resolve);
      scheduleNoteAttributesFlush();
    });
  };

  const enqueueNoteAttributesRef = useRef(enqueueNoteAttributes);
  enqueueNoteAttributesRef.current = enqueueNoteAttributes;

  useEffect(() => {
    const handleCustomAttributes = (event: MessageEvent) => {
      if (event?.data?.type !== eventTypes.FLO_CUSTOM_ATTRIBUTES) return;
      // Order/event attributes only mean something where a checkout exists.
      if (contextRef.current !== "checkout" && contextRef.current !== "cart") return;
      const payload = event?.data?.payload ?? {};

      // Events first and synchronously - it needs no request, and an event
      // fired straight after the merchant's call should already carry it.
      setCustomAttributesRef.current(payload?.events ?? {});

      const incoming: NoteAttributesType[] = Array.isArray(payload?.order) ? payload.order : [];
      if (!incoming.length && !pushedNoteAttributeNamesRef.current.size) return;

      // The snapshot is a full statement of what the store script owns, so a
      // name it has stopped sending is a removal.
      const droppedNames = findDroppedNames(pushedNoteAttributeNamesRef.current, incoming);
      pushedNoteAttributeNamesRef.current = new Set(incoming.map((attribute) => attribute.name));

      enqueueNoteAttributesRef.current({ upserts: incoming, removals: droppedNames });
    };

    // A debounced write would otherwise be lost when the shopper leaves or the
    // iframe is closed mid-flow.
    const flushBeforeUnload = () => {
      if (!pendingNoteOpsRef.current) return;
      if (customAttributesTimerRef.current) {
        clearTimeout(customAttributesTimerRef.current);
      }
      flushNoteAttributesRef.current();
    };

    window.addEventListener("message", handleCustomAttributes);
    window.addEventListener("pagehide", flushBeforeUnload);

    return () => {
      window.removeEventListener("message", handleCustomAttributes);
      window.removeEventListener("pagehide", flushBeforeUnload);
      if (customAttributesTimerRef.current) {
        clearTimeout(customAttributesTimerRef.current);
      }
    };
  }, []);

  const {
    state: { userConsents },
    actions: { updateConsent },
  } = useUserConsentContext();
  const consentSource = (
    { checkout: "CHECKOUT", cart: "CART", sso: "SSO", account: "ACCOUNTS_PAGE" } as const
  )[context];
  const updateConsentRef = useRef((updates: ConsentUpdate[]) => updateConsent(updates, { source: consentSource }));
  updateConsentRef.current = (updates: ConsentUpdate[]) => updateConsent(updates, { source: consentSource });
  // Consent is only exposed to a logged-in shopper whose consents have loaded.
  const isConsentAvailable = Boolean(user?.uid) && Boolean(userConsents);

  const accountBindingRef = useRef<AccountWindowBinding | null>(null);
  const accountsConfigRef = useRef(ssoMetadata?.accounts);
  accountsConfigRef.current = ssoMetadata?.accounts;

  // Created once so scripts that read window.Shopflo.actions a single time keep
  // calling the latest implementation through the refs.
  const windowActions = useRef({
    // ── Common: every context ──
    common: {
      redirect: (url: string) => {
        if (!url) return;
        publishPostMessage(eventTypes.PARENT_REDIRECT, { redirectUrl: url });
      },
      setLocalStorage: (key: string, value: unknown) => setRuleLocalStorageItem(key, value),
    },
    // ── Common, logged in only ──
    consent: {
      updateConsent: (updates: ConsentUpdate[]) => updateConsentRef.current(updates ?? []),
    },
    // ── Checkout & cart ──
    checkout: {
      addItem: async (
        items: Array<{ variantId: string; quantity: number }>,
        { skipCartAdd }: { skipCartAdd: boolean } = { skipCartAdd: false },
      ) => {
        await addItemRef.current(items, !Boolean(skipCartAdd));
      },
      removeItem: async (items: Array<{ variantId?: string; index?: number; quantity?: number }>) => {
        await removeItemRef.current(items);
      },
      applyCoupon: (code: string) => applyCouponRef.current(code),
      addNoteAttributes: (noteAttributes: Array<{ name: string; value: string }>) =>
        addNoteAttributesRef.current(noteAttributes),
      editNoteAttributes: (noteAttributes: Array<{ name: string; value: string }>) =>
        editNoteAttributesRef.current(noteAttributes),
      removeNoteAttributes: (noteAttributes: Array<string | { name: string }>) =>
        removeNoteAttributesRef.current(noteAttributes),
      applyShopifyGiftCard: (code: string) => applyShopifyGiftCardRef.current(code),
      removeShopifyGiftCard: () => removeShopifyGiftCardRef.current(),
      setItemLevelETD: (items: ItemLevelETDType) => setItemLevelETDRef.current(items),
    },
    // ── SSO (marketing popup) ──
    sso: {
      // Same message the popup's own close button sends.
      closePopup: () => publishPostMessage(eventTypes.FLO_MARKETING_POPUP_CLOSED, {}),
    },
    // ── Account ──
    account: {
      openPage: (page: string) => {
        const binding = accountBindingRef.current;
        if (!binding) return false;
        const isKnownPage = (Object.values(AccountActivePage) as string[]).includes(page);
        const isCustomPage = getAccountCustomPages(accountsConfigRef.current).some((p) => p.type === page);
        if (!isKnownPage && !isCustomPage) return false;
        binding.openPage(page);
        return true;
      },
      closeAccount: () => accountBindingRef.current?.close(),
    },
  }).current;

  const closeCart = () => {
    setCheckoutModal("NONE");
    closePopup();
    publishPostMessage(eventTypes.CLOSE_CART_IFRAME, {});
  };

  const contextActions = {
    checkout: windowActions.checkout,
    cart: { ...windowActions.checkout, closeCart },
    sso: windowActions.sso,
    account: windowActions.account,
  }[context];

  const contextState = {
    checkout: { checkout: rawCheckoutObject },
    cart: { checkout: rawCheckoutObject },
    sso: {},
    account: { activePage: accountBindingRef.current?.activePage },
  }[context];

  // Fields are mutated in place rather than the objects replaced, so a script
  // holding window.Shopflo.state or .actions keeps seeing current values. A
  // context change starts a fresh object so no other context's keys linger.
  const syncWindow = () => {
    if (!window.Shopflo || window.Shopflo.context !== context) {
      window.Shopflo = { context, state: {}, actions: {} } as unknown as ShopfloWindow;
    }
    Object.assign(window.Shopflo.actions, windowActions.common, contextActions);
    Object.assign(window.Shopflo.state, { merchantAttributes: merchant, uiAttributes, user }, contextState);
    if (isConsentAvailable) {
      Object.assign(window.Shopflo.actions, windowActions.consent);
      window.Shopflo.state.consents = userConsents ?? undefined;
    } else {
      // Logged out (or logged out since): the keys disappear, not just go undefined.
      delete window.Shopflo.actions.updateConsent;
      delete window.Shopflo.state.consents;
    }
  };
  const syncWindowRef = useRef(syncWindow);
  syncWindowRef.current = syncWindow;

  // Assign synchronously during render (parent renders before children) so that
  // child effects - custom component scripts - always read the latest values.
  syncWindow();

  useEffect(() => {
    // Restore after a cleanup that ran on remount (StrictMode).
    syncWindowRef.current();
    return () => {
      delete (window as any).Shopflo;
    };
  }, []);

  return (
    <AccountWindowBindingContext.Provider value={accountBindingRef}>
      {children}
      {(isLoading || isAddItemLoading) && <OverlaySpinner />}
    </AccountWindowBindingContext.Provider>
  );
};

export default WindowActionsProvider;
